import type {
  WorldRoomDirectory,
  WorldRoomProjection,
  WorldSessionProjection,
} from "@cleo/contracts";

/**
 * The Room Rail's one fold (register RM, the path of record: "one row per
 * open room, expanding to its sessions"; the study of 2026-09-03): rooms over
 * sessions over sub-sessions, and the parked group beside them.
 *
 * This is deliberately the ONLY place the rail knows the World's shape.
 * Lifecycle comes from the World's session.retired fold. Engagement records
 * whether the person is present and is independent of retirement.
 *
 * Laws:
 * - Both room kinds share this fold. The dock chooses the general region
 *   or the skill chip as the room header; neither hides session machinery.
 * - A room's rows are its ROOT sessions (parentId = the room) in born order;
 *   a session's children are its sub-sessions (parentId = the session).
 *   Threads never appear: a thread lives in the session-view.
 * - Retired sessions remain addressable in the memory group. Headless open
 *   sessions remain open; a pending retirement is still open.
 * - Parked entries group by room, most recently born first: the room's
 *   memory reads newest at the top.
 */

export interface RoomRailSessionNode {
  readonly session: WorldSessionProjection;
  /** Open sub-sessions, in born order. */
  readonly children: ReadonlyArray<RoomRailSessionNode>;
  /** The session stands in the addressed lineage. */
  readonly addressed: boolean;
}

export interface RoomRailRoom {
  readonly room: WorldRoomProjection;
  /** The World's standing general room, born at seq 1. */
  readonly standing: boolean;
  readonly sessions: ReadonlyArray<RoomRailSessionNode>;
}

export interface RoomRailParkedRoom {
  readonly room: WorldRoomProjection;
  /** Retired sessions of this room, any depth, newest first. */
  readonly sessions: ReadonlyArray<WorldSessionProjection>;
}

export interface RoomRailGroups {
  readonly rooms: ReadonlyArray<RoomRailRoom>;
  readonly parked: ReadonlyArray<RoomRailParkedRoom>;
  /** Every session id on the addressed lineage, the active session included. */
  readonly addressed: ReadonlySet<string>;
}

/** The addressed lineage: the active session and every ancestor session up to its room. */
export function addressedLineage(
  index: WorldRoomDirectory,
  activeSessionId: string | null,
): Set<string> {
  const byId = new Map(index.sessions.map((session) => [session.sessionId, session]));
  const lineage = new Set<string>();
  let cursor = activeSessionId === null ? undefined : byId.get(activeSessionId);
  while (cursor !== undefined && !lineage.has(cursor.sessionId)) {
    lineage.add(cursor.sessionId);
    cursor = byId.get(cursor.parentId);
  }
  return lineage;
}

const byBorn = (a: { readonly bornSeq: number }, b: { readonly bornSeq: number }) =>
  a.bornSeq - b.bornSeq;

export function projectRoomRailGroups(
  index: WorldRoomDirectory,
  activeSessionId: string | null,
): RoomRailGroups {
  const addressed = addressedLineage(index, activeSessionId);
  const childrenByParent = new Map<string, WorldSessionProjection[]>();
  for (const session of index.sessions) {
    const siblings = childrenByParent.get(session.parentId);
    if (siblings === undefined) childrenByParent.set(session.parentId, [session]);
    else siblings.push(session);
  }
  const open = (session: WorldSessionProjection): boolean => session.lifecycle !== "retired";
  const node = (session: WorldSessionProjection): RoomRailSessionNode => ({
    session,
    addressed: addressed.has(session.sessionId),
    children: (childrenByParent.get(session.sessionId) ?? []).filter(open).sort(byBorn).map(node),
  });
  const orderedRooms = index.rooms.filter((room) => room.archived !== true).sort(byBorn);
  const rooms: RoomRailRoom[] = orderedRooms.map((room) => ({
    room,
    standing: room.roomId === index.rootRoomId,
    sessions: (childrenByParent.get(room.roomId) ?? []).filter(open).sort(byBorn).map(node),
  }));
  const parked: RoomRailParkedRoom[] = orderedRooms
    .map((room) => ({
      room,
      sessions: index.sessions
        .filter((session) => session.roomId === room.roomId && session.lifecycle === "retired")
        .sort((a, b) => b.bornSeq - a.bornSeq),
    }))
    .filter((entry) => entry.sessions.length > 0);
  return { rooms, parked, addressed };
}

/** Every open session under a node, depth-first — the room's live count. */
export function flattenRailNodes(
  nodes: ReadonlyArray<RoomRailSessionNode>,
): ReadonlyArray<RoomRailSessionNode> {
  const out: RoomRailSessionNode[] = [];
  const walk = (list: ReadonlyArray<RoomRailSessionNode>) => {
    for (const entry of list) {
      out.push(entry);
      walk(entry.children);
    }
  };
  walk(nodes);
  return out;
}
